import React from "react";
import { useQuery } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { Bus, Plane, Mountain, Church, Music, Heart, ArrowRight, CheckCircle2 } from "lucide-react";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import EventCard from "@/components/EventCard";

const HERO_IMG =
  "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjB0b3VyaXN0JTIwYnVzJTIwY29hY2glMjBleHRlcmlvcnxlbnwwfHx8fDE3ODQ1MzU0MjR8MA&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Bus, title: "Noleggio Autobus con Conducente", text: "Autobus da 4 a 54 posti con autisti qualificati, per ogni esigenza di gruppo." },
  { icon: Plane, title: "Transfer Aeroporti, Stazioni e Porti", text: "Trasferimenti puntuali e confortevoli da e per aeroporti, stazioni e porti." },
  { icon: Mountain, title: "Escursioni Giornaliere", text: "Gite ed escursioni in Italia e all'estero, organizzate su misura per te." },
  { icon: Church, title: "Parrocchie, Cral e Associazioni", text: "Viaggi su commissione per gruppi, associazioni, cral e privati." },
  { icon: Music, title: "Concerti ed Eventi", text: "Trasporto dedicato per concerti ed eventi, andata e ritorno in sicurezza." },
  { icon: Heart, title: "Matrimoni & Cerimonie", text: "Un trasporto su misura e confortevole per i tuoi invitati nel giorno più bello." },
];

const Home = () => {
  const { data: events = [], isLoading } = useQuery({
    queryKey: ["events"],
    queryFn: async () => (await api.get("/events")).data,
  });

  return (
    <div className="min-h-screen bg-[#050505] text-white">
      <Navbar />

      {/* HERO */}
      <section className="relative min-h-screen flex items-center pt-24 pb-16 overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_IMG} alt="Autobus turistico Cardillo Viaggi" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#050505] via-[#050505]/85 to-[#050505]/40" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#050505] via-transparent to-[#050505]/60" />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 md:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            className="max-w-2xl"
          >
            <span className="inline-block bg-[#FFF200]/10 text-[#FFF200] px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-widest mb-6">
              Agenzia Viaggi &amp; Noleggio Autobus
            </span>
            <h1 className="font-display text-5xl md:text-7xl font-bold tracking-tighter leading-[0.95]">
              Il tuo viaggio,
              <br />
              <span className="text-[#FFF200]">su misura.</span>
            </h1>
            <p className="text-lg md:text-xl text-white/70 mt-6 leading-relaxed max-w-xl">
              Da Vietri di Potenza in tutta Italia e all'estero. Autisti qualificati, autobus da 4
              a 54 persone e l'esperienza di una ditta a conduzione familiare.
            </p>
            <div className="flex flex-wrap gap-4 mt-9">
              <button
                data-testid="hero-cta-proposte"
                onClick={() => document.querySelector("#proposte")?.scrollIntoView({ behavior: "smooth" })}
                className="flex items-center gap-2 bg-[#FFF200] text-black font-semibold px-7 py-3.5 rounded-full hover:bg-[#FFF200]/90 transition-colors"
              >
                Scopri le proposte <ArrowRight size={18} />
              </button>
              <button
                data-testid="hero-cta-servizi"
                onClick={() => document.querySelector("#servizi")?.scrollIntoView({ behavior: "smooth" })}
                className="bg-white/5 border border-white/15 text-white font-semibold px-7 py-3.5 rounded-full hover:bg-white/10 transition-colors"
              >
                I nostri servizi
              </button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* PROPOSTE */}
      <section id="proposte" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-12">
            <div>
              <span className="text-[#FFF200] text-sm font-bold uppercase tracking-widest">In evidenza</span>
              <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2">
                Viaggi ed eventi disponibili
              </h2>
            </div>
            <p className="text-white/60 max-w-sm">
              Concerti, visite guidate, escursioni e viaggi organizzati. Prenota e paga comodamente
              online.
            </p>
          </div>

          {isLoading ? (
            <div className="text-center text-white/40 py-20">Caricamento proposte...</div>
          ) : events.length === 0 ? (
            <div
              data-testid="empty-events"
              className="text-center py-20 border border-dashed border-white/10 rounded-2xl"
            >
              <p className="text-white/50 text-lg">Nessuna proposta disponibile al momento.</p>
              <p className="text-white/30 text-sm mt-2">
                Contattaci per organizzare il tuo viaggio su misura.
              </p>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="events-grid">
              {events.map((ev, i) => (
                <EventCard key={ev.id} event={ev} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* SERVIZI */}
      <section id="servizi" className="py-20 md:py-32 bg-[#0a0a0a] border-y border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="max-w-2xl mb-14">
            <span className="text-[#FFF200] text-sm font-bold uppercase tracking-widest">Cosa offriamo</span>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2">
              Noleggio autobus con conducente
            </h2>
            <p className="text-white/60 mt-4 leading-relaxed">
              Attività di noleggio a fini turistici su tutto il territorio nazionale ed estero. I
              servizi offerti sono innumerevoli.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {services.map((s, i) => (
              <motion.div
                key={s.title}
                data-testid={`service-card-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: (i % 3) * 0.08 }}
                className="bg-[#121212] border border-[#27272A] rounded-2xl p-7 hover:border-[#FFF200]/40 transition-colors duration-300"
              >
                <div className="w-12 h-12 rounded-xl bg-[#FFF200]/10 flex items-center justify-center mb-5">
                  <s.icon className="text-[#FFF200]" size={22} />
                </div>
                <h3 className="text-lg font-display font-semibold mb-2">{s.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{s.text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CHI SIAMO */}
      <section id="chi-siamo" className="py-20 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-2 gap-14 items-center">
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="relative rounded-3xl overflow-hidden aspect-[4/3]"
          >
            <img
              src="https://images.pexels.com/photos/29586609/pexels-photo-29586609.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
              alt="Il nostro autobus"
              className="w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#050505]/60 to-transparent" />
          </motion.div>

          <div>
            <span className="text-[#FFF200] text-sm font-bold uppercase tracking-widest">Chi siamo</span>
            <h2 className="font-display text-3xl md:text-5xl font-bold tracking-tight mt-2 leading-tight">
              L'esperienza di una <span className="text-[#FFF200]">ditta familiare</span>
            </h2>
            <p className="text-white/65 mt-5 leading-relaxed">
              Cardillo Viaggi vi propone tutta l'esperienza del suo personale qualificato e la
              qualità di un servizio che soddisfa impeccabilmente le esigenze dei suoi clienti. Con
              questi presupposti avrete la garanzia di un risultato impeccabile, offrendo ai vostri
              invitati un trasporto su misura e confortevole.
            </p>
            <ul className="mt-8 space-y-3">
              {[
                "Autisti qualificati ed esperti",
                "Autobus da 4 a 54 persone",
                "Territorio nazionale ed estero",
                "Soluzioni su misura per ogni esigenza",
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-white/80">
                  <CheckCircle2 className="text-[#FFF200] shrink-0" size={19} /> {item}
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;
